const busyTriggers = new WeakSet();
let runtimePromise;
let runtimeSpecifier;

export function shouldEnhanceQuickBuyClick(event, trigger) {
  if (event.defaultPrevented || (event.button ?? 0) !== 0) return false;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
  if (trigger.hasAttribute?.('download')) return false;
  const target = trigger.getAttribute?.('target');
  return !target || target === '_self';
}

export function resolveQuickBuyTarget(trigger, baseUrl) {
  const card = trigger.closest?.('product-card');
  if (!card) return null;
  const cardLink = card.refs?.productCardLink ?? card.querySelector?.('[ref="productCardLink"]');
  const href = cardLink?.href ?? cardLink?.getAttribute?.('href');
  if (!href) return null;

  const pageUrl = new URL(baseUrl);
  const productUrl = new URL(href, pageUrl);
  if (productUrl.origin !== pageUrl.origin || !['http:', 'https:'].includes(productUrl.protocol)) return null;

  return {
    productUrl,
    productId: card.dataset?.productId || cardLink.dataset?.productId || trigger.dataset?.productId || '',
  };
}

export function installQuickBuyLoader(doc = document, locationObject = window.location) {
  if (!doc.querySelector('[data-tfp-quick-buy-host]')?.dataset.runtime) return false;
  const captureRoot = doc.defaultView ?? doc;

  captureRoot.addEventListener('click', async (event) => {
    const trigger = event.target?.closest?.('a[data-quick-buy-trigger]');
    if (!trigger || !shouldEnhanceQuickBuyClick(event, trigger)) return;
    const initialHost = doc.querySelector('[data-tfp-quick-buy-host]');
    const requestedRuntime = initialHost?.dataset.runtime;
    if (!requestedRuntime) return;

    const target = resolveQuickBuyTarget(trigger, locationObject.href);
    if (!target) return;

    event.preventDefault();
    if (busyTriggers.has(trigger)) return;

    busyTriggers.add(trigger);
    trigger.setAttribute('aria-busy', 'true');
    trigger.dataset.quickBuyBusy = 'true';

    try {
      if (!runtimePromise || runtimeSpecifier !== requestedRuntime) {
        runtimeSpecifier = requestedRuntime;
        runtimePromise = import(requestedRuntime);
      }
      const pendingRuntime = runtimePromise;
      let runtime;
      try {
        runtime = await pendingRuntime;
      } catch (error) {
        if (runtimePromise === pendingRuntime) {
          runtimePromise = undefined;
          runtimeSpecifier = undefined;
        }
        throw error;
      }

      const currentHost = doc.querySelector('[data-tfp-quick-buy-host]');
      if (!currentHost?.isConnected || currentHost.dataset.runtime !== requestedRuntime) {
        throw new Error('Quick buy host changed');
      }
      await runtime.openQuickBuy({ host: currentHost, trigger, ...target });
    } catch {
      locationObject.assign(target.productUrl.href);
    } finally {
      busyTriggers.delete(trigger);
      trigger.removeAttribute('aria-busy');
      delete trigger.dataset.quickBuyBusy;
    }
  }, { capture: true });

  return true;
}

if (typeof document !== 'undefined' && typeof window !== 'undefined') installQuickBuyLoader();

